import { ReactNode, useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const DRAG_CLOSE_THRESHOLD_PX = 80;

interface CatalogSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  headerAction?: ReactNode;
  footer?: ReactNode;
  closeLabel: string;
  children: ReactNode;
}

export function CatalogSheet({ open, onClose, title, headerAction, footer, closeLabel, children }: CatalogSheetProps) {
  const [dragY, setDragY] = useState(0);
  const handleRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useBodyScrollLock(open);
  useFocusTrap(dialogRef, open, onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    let startY = 0;
    let dragging = false;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      dragging = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!dragging) return;
      const delta = e.touches[0].clientY - startY;
      if (delta > 0) {
        e.preventDefault();
        setDragY(delta);
      }
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      setDragY((current) => {
        if (current > DRAG_CLOSE_THRESHOLD_PX) onCloseRef.current();
        return 0;
      });
    };

    handle.addEventListener('touchstart', onTouchStart, { passive: true });
    handle.addEventListener('touchmove', onTouchMove, { passive: false });
    handle.addEventListener('touchend', onTouchEnd);

    return () => {
      handle.removeEventListener('touchstart', onTouchStart);
      handle.removeEventListener('touchmove', onTouchMove);
      handle.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div
      className={`lg:hidden fixed inset-0 z-sheet transition-opacity duration-300 ${
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      inert={!open}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`absolute bottom-0 left-0 right-0 sm:max-w-xl sm:mx-auto bg-white rounded-t-2xl max-h-[85vh] flex flex-col focus:outline-none ${
          dragY === 0 ? 'transition-transform duration-300 ease-out' : ''
        } ${open ? 'translate-y-0' : 'translate-y-full'}`}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined}
      >
        <div ref={handleRef} className="flex justify-center pt-2 pb-1 touch-none" aria-hidden="true">
          <span className="w-10 h-1.5 bg-champagne-dark rounded-full" />
        </div>

        <div className="flex items-center justify-between pl-4 pr-1 border-b border-champagne">
          <h2 className="font-display font-semibold text-text-dark text-base py-2">{title}</h2>
          <div className="flex items-center">
            {headerAction}
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="w-11 h-11 flex items-center justify-center rounded-full text-text-medium hover:text-text-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto overscroll-y-contain px-4 pt-4 ${footer ? 'pb-4' : 'pb-safe'}`}>{children}</div>

        {footer && <div className="border-t border-champagne px-4 pt-3 pb-safe">{footer}</div>}
      </div>
    </div>
  );
}
